import React from 'react';

import { useClaim } from '~/components/ClaimContainer';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';
import useDataFetcher from '~/components/useDataFetcher';

interface InsightsUndismissedCountResponse {
  undismissed_count: number;
}

interface ClaimInsightsCountContextValue {
  /** Undismissed, non-deleted insight versions on this claim — every version, not one per key. */
  undismissedCount: number;
  reloadUndismissedCount: () => Promise<void>;
}

export const ClaimInsightsCountContext = React.createContext<ClaimInsightsCountContextValue>({
  undismissedCount: 0,
  reloadUndismissedCount: async () => {},
});

export const useClaimInsightsCount = (): ClaimInsightsCountContextValue => React.useContext(ClaimInsightsCountContext);

/**
 * The claim-level count behind the "Clive Insights (N)" sidebar tab badge.
 *
 * Mounted above the sidebar rather than inside the tab: the badge has to show a number before
 * anyone opens Insights, and `InsightsDataProvider` only mounts once that tab is active. The
 * list read that provider does is latest-per-key, so it could not produce this number anyway.
 */
export const ClaimInsightsCountContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { claim } = useClaim({ claimRequired: true });
  const { isClaimChatEnabled, isCliveConfigEnabled } = useCliveClaimContext();

  // READ, matching the verb the GET route enforces server-side, and the same verb
  // InsightsDataProvider gates its own reads on.
  const hasInsightsPermission = useHasPermission({
    action: PERMISSION_ACTIONS.CLIVE_CLAIM_INSIGHTS,
    verb: PERMISSION_VERBS.READ,
  });

  // The same conjunction the `clive_insights` tab getter uses for `shouldDisplay`. There is no
  // badge to feed when the tab is not rendered, so there is no request to make either.
  const shouldFetch = hasInsightsPermission && isClaimChatEnabled && isCliveConfigEnabled('clive_insights_claim_tab');

  const { data, reloadData } = useDataFetcher<InsightsUndismissedCountResponse>(
    `/api/v1/claims/${claim.id}/insights/statuses/count`,
    {},
    // A tab badge is decorative: a failure here degrades to the plain "Clive Insights" label, and
    // toasting every claim page open over a number nobody asked for would be worse than the miss.
    { shouldFetch, shouldReportAxiosError: false }
  );

  const reloadUndismissedCount = React.useCallback(async () => {
    if (!shouldFetch) {
      return;
    }
    await reloadData();
  }, [shouldFetch, reloadData]);

  const value = React.useMemo(
    () => ({ undismissedCount: data?.undismissed_count ?? 0, reloadUndismissedCount }),
    [data, reloadUndismissedCount]
  );

  return <ClaimInsightsCountContext.Provider value={value}>{children}</ClaimInsightsCountContext.Provider>;
};
